import { type FC, useMemo } from "react";

import { startCase } from "lodash";
import moment from "moment";

import { Badge, Button } from "@alpacahq/ui/old";
import {
  DataGrid,
  type TableColumn,
  useDataGrid,
} from "@alpacahq/ui/old/datagrid";

import type { CryptoAsset, CryptoTransfer } from "~/v2/api/rest/cryptoWallet";
import { getPrettyAmount, getPrettyCash } from "~/v2/utils/formatting";
import {
  createAssetMap,
  createCryptoAssetSymbol,
  getTransactionLink,
  getTransferStatusBadgeProps,
  transformAddress,
} from "../../coinTransfers/util";

const AMOUNT_MAX_FRACTION_DIGITS = 12;

type CryptoTransferHistoryTableProps = {
  transfers: CryptoTransfer[];
  assets: CryptoAsset[];
  isLoading?: boolean;
};

export const CryptoTransferHistoryTable: FC<
  CryptoTransferHistoryTableProps
> = ({ transfers, assets, isLoading }) => {
  const assetMap = useMemo(() => createAssetMap(assets), [assets]);

  const onTransferTransactionClicked = (transferLink: string) => () =>
    window.open(transferLink, "_blank", "noopener,noreferrer");

  const sortedTransfers = useMemo(
    () =>
      [...transfers].sort((a, b) =>
        moment(b.created_at).diff(moment(a.created_at)),
      ),
    [transfers],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: onTransferTransactionClicked is a caller-supplied handler, normally an inline arrow, so listing it would rebuild the whole column set on every render.
  const columns = useMemo<TableColumn<CryptoTransfer>[]>(
    () => [
      {
        key: "created_at",
        name: "Date",
        cellRenderer: ({ created_at }) => (
          <div className="flex flex-col gap-0.5">
            <div className="text-gray-700 text-sm">
              {moment(created_at).format("MM/DD/YYYY")}
            </div>
            <div className="font-medium text-gray-500 text-xs">
              {moment(created_at).format("hh:mm A")}
            </div>
          </div>
        ),
      },
      {
        key: "status",
        name: "Status",
        cellRenderer: ({ status }) => (
          <Badge {...getTransferStatusBadgeProps(status)}>
            {startCase(status?.toLowerCase())}
          </Badge>
        ),
      },
      {
        key: "type",
        name: "Type",
        cellRenderer: ({ direction }) => (
          <div className="text-gray-700 text-sm">
            {direction === "INCOMING" ? "In" : "Out"}
          </div>
        ),
      },
      {
        key: "asset",
        name: "Crypto",
        cellRenderer: ({ asset }) => (
          <div className="text-gray-700 text-sm">{asset}</div>
        ),
      },
      {
        key: "network",
        name: "Network",
        cellRenderer: ({ chain }) => (
          <div className="text-gray-700 text-sm">{chain}</div>
        ),
      },
      {
        key: "amount",
        name: "Amount",
        alignRight: true,
        cellRenderer: ({ amount }) => (
          <div className="text-right font-mono text-gray-700 text-sm">
            {getPrettyAmount(amount, 0, AMOUNT_MAX_FRACTION_DIGITS)}
          </div>
        ),
      },
      {
        key: "from_address",
        name: "From Address",
        cellRenderer: (transfer) => {
          const assetSymbol = createCryptoAssetSymbol(
            transfer.asset,
            transfer.chain,
          );
          const asset = assetMap[assetSymbol];
          const transactionLink = getTransactionLink({ transfer, asset });
          return (
            <div className="flex flex-col items-end justify-center gap-1">
              <div className="font-mono text-gray-700 text-sm">
                {transformAddress(transfer.from_address ?? "")}
              </div>
              {transactionLink && (
                <Button
                  variant="v2/ghost"
                  size="v2/small"
                  onClick={onTransferTransactionClicked(transactionLink)}
                  type="button"
                  className="h-3.5 py-0"
                >
                  Transaction
                </Button>
              )}
            </div>
          );
        },
        alignRight: true,
      },
      {
        key: "to_address",
        name: "To Address",
        cellRenderer: ({ to_address }) => (
          <div className="font-mono text-gray-700 text-sm">
            {transformAddress(to_address ?? "")}
          </div>
        ),
      },
      {
        key: "fees",
        name: "Fee",
        alignRight: true,
        cellRenderer: ({ fees }) => (
          <div className="text-right font-mono text-gray-700 text-sm">
            {getPrettyAmount(fees, 0, AMOUNT_MAX_FRACTION_DIGITS)}
          </div>
        ),
      },
      {
        key: "network_fee",
        name: "Network Fee",
        alignRight: true,
        cellRenderer: ({ network_fee }) => (
          <div className="text-right font-mono text-gray-700 text-sm">
            {getPrettyAmount(network_fee, 0, AMOUNT_MAX_FRACTION_DIGITS)}
          </div>
        ),
      },
      {
        key: "usd_value",
        name: "USD",
        alignRight: true,
        cellRenderer: ({ usd_value }) => (
          <div className="text-right font-mono text-gray-700 text-sm">
            {getPrettyCash(usd_value)}
          </div>
        ),
      },
    ],
    [assetMap],
  );

  const tableProps = useDataGrid({
    rows: sortedTransfers,
    columns,
  });

  return (
    <DataGrid
      variant="modern"
      key="crypto-transfer-history"
      isLoading={isLoading || tableProps.isLoading}
      rows={tableProps.rows}
      columns={tableProps.columns}
      emptyMessage="No crypto transfers found."
    />
  );
};
